import { Injectable, type Signal, signal } from '@angular/core';

import type { AppShellTab } from '@efficiently/client-ui-app-shell';

/** What the row becomes once the group is left. */
const NO_TABS: readonly AppShellTab[] = [];

/**
 * The Platform Administrator row's contents, published by the group's route host and rendered by
 * the Design section's root (EFF-24841).
 *
 * **Why a store and not `AppShellRegistry`.** The registry feeds the shell's own strip, which is a
 * full-width band ABOVE the rail and the side panel — and this row does not belong there. The
 * design puts it inside the content column, level with the rail's first button, which means it is
 * rendered by `PagesComponent` rather than by the shell. `PlatformAdminComponent` is a descendant
 * of that component, so the two cannot pass it down through inputs; a root signal is the channel,
 * and the host's LIFETIME is what the row follows — present while the group is open, gone the
 * moment it is left, with no URL matching duplicated anywhere.
 *
 * `providedIn: 'root'` is load-bearing for the same reason it is on the registry: the writer lives
 * inside a lazily loaded subtree, and a store provided on a component would sit in an element
 * injector the reader above it could never see.
 */
@Injectable({ providedIn: 'root' })
export class PlatformAdminTabsStore {
	private readonly current = signal<readonly AppShellTab[]>(NO_TABS);

	private readonly active = signal(false);

	public readonly tabs: Signal<readonly AppShellTab[]> = this.current.asReadonly();

	/**
	 * Whether the Platform Administrator route group is currently open.
	 *
	 * **Why this is not derivable from `tabs`.** A user permitted none of the group's children
	 * publishes an EMPTY tab array, which is indistinguishable from having left the group
	 * altogether. This flag follows the group host's LIFETIME instead, so "inside the group" holds
	 * even with nothing to render in the row — which is what the header title needs in order to
	 * show the admin label.
	 *
	 * It reports where the user is, never what the user may do: the `canMatch` permission guards on
	 * the children remain the only gate, and nothing may read this as an authorization decision.
	 */
	public readonly isActive: Signal<boolean> = this.active.asReadonly();

	public set(tabs: readonly AppShellTab[]): void {
		this.current.set(tabs);
	}

	/** Marks the group open. Idempotent — the host calls it from an effect that re-runs freely. */
	public activate(): void {
		this.active.set(true);
	}

	public clear(): void {
		this.current.set(NO_TABS);
		this.active.set(false);
	}
}
